import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Typography,
} from '@mui/material';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useGetContractBySellerQuery } from '@/apis/extended/contractApi';
import { useSignContractAsSellerMutation } from '@/apis/extended/contractRatificationApi';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CommonPrevNextButtons } from '../Common/CommonContract/CommonPrevNextButtons';
import { CommonFormControl } from '../Common/CommonFormControl';
import UserSignatureCanvas from '../Contract/Components/SignatureCanvas/UserSignatureCanvas';

const initialForm = {
  name: '',
  signature: '',
};

type ContractDetailSellerPostNegotiationSignProps = {
  uid: string;
};

export const ContractDetailSellerPostNegotiationSign = ({
  uid,
}: ContractDetailSellerPostNegotiationSignProps) => {
  const navigate = useNavigate();
  const { data: contract, isLoading: isLoadingContract } =
    useGetContractBySellerQuery(uid);
  const [signContract, { isLoading: isSigning }] =
    useSignContractAsSellerMutation();

  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    const sellerSign = contract?.sellerSign;
    if (!sellerSign) return;
    setForm({
      name: sellerSign.name || '',
      signature: sellerSign.signature || '',
    });
  }, [contract]);

  const isValid = useMemo(() => {
    return Boolean(form.name && form.signature);
  }, [form]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!isValid || !contract?._id) return;

    try {
      const payload = {
        contractId: contract._id.toString(),
        name: form.name,
        signature: form.signature,
      };

      await signContract(payload).unwrap();

      toast.success(
        'Contract signed successfully! The contract is now fully ratified.'
      );

      // Navigate to success page or dashboard
      navigate(`/seller/property-dashboard/${contract._id.toString()}`);
    } catch (error: any) {
      console.error('❌ Error signing contract:', error);
      console.error('Error details:', {
        status: error?.status,
        data: error?.data,
        message: error?.message,
        fullError: error,
      });

      const errorMessage =
        error?.data?.error ||
        error?.data?.message ||
        error?.data ||
        'Failed to sign contract. Please try again.';
      toast.error(errorMessage);
    }
  };

  if (isLoadingContract) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Loading contract...</Typography>
      </Box>
    );
  }

  if (!contract) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography color="error">Contract not found</Typography>
      </Box>
    );
  }

  // Check if contract is in the right status
  const canSign =
    contract.contractStatus === 'awaiting_signatures' ||
    contract.contractStatus === 'awaiting_seller_signature';

  if (!canSign) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
        <Alert className="border-blue-500/50">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Contract Not Ready for Signature</AlertTitle>
          <AlertDescription>
            This contract is currently in status:{' '}
            <strong>{contract.contractStatus}</strong>
            <br />
            The contract must be in "awaiting_signatures" or
            "awaiting_seller_signature" status before you can sign.
          </AlertDescription>
        </Alert>
      </Box>
    );
  }

  if (contract.sellerSign?.isStored) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
        <Alert className="border-green-500/50 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertTitle>Already Signed</AlertTitle>
          <AlertDescription>
            You have already signed this contract.
            {contract.isRatified ? (
              <span> The contract is now fully ratified!</span>
            ) : (
              <span>
                {' '}
                Waiting for buyer signature to complete ratification.
              </span>
            )}
          </AlertDescription>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Card sx={{ mt: 2 }}>
        <CardHeader
          title="Sign Contract After Negotiations"
          avatar={<i className="fa-light fa-file-signature" />}
        />
        <CardContent>
          <Alert className="mb-6 border-green-500/50 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertTitle>Contract Ready for Your Signature</AlertTitle>
            <AlertDescription>
              All negotiations have been completed and the contract has been
              finalized.
              {contract.contractStatus === 'awaiting_seller_signature' &&
                contract.buyerSign?.isStored && (
                  <span>
                    {' '}
                    The buyer has already signed. Your signature will complete
                    the ratification.
                  </span>
                )}
            </AlertDescription>
          </Alert>

          <Typography variant="h6" sx={{ mb: 2 }}>
            Contract Summary
          </Typography>

          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Purchase Price:</strong> $
              {contract.offerSpecifics?.offerPrice?.toLocaleString() || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Closing Date:</strong>{' '}
              {contract.offerSpecifics?.closingDate ||
                contract.timeline?.closingDate ||
                'N/A'}
            </Typography>
            {contract.offerSpecifics?.earnestMoneyDeposit && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Earnest Money:</strong> $
                {contract.offerSpecifics.earnestMoneyDeposit.toLocaleString()}
              </Typography>
            )}
            {contract.offerSpecifics?.buyerAgentCompensation && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Buyer Agent Compensation:</strong>{' '}
                {contract.offerSpecifics.buyerAgentCompensation.toLocaleString()}
                %
              </Typography>
            )}
            {contract.offerSpecifics?.sellerAssistanceClosingCosts && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Seller Assistance:</strong> $
                {contract.offerSpecifics.sellerAssistanceClosingCosts.toLocaleString()}
              </Typography>
            )}
            {contract.offerSpecifics?.additionalSellerCredit && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Additional Seller Credit:</strong> $
                {contract.offerSpecifics.additionalSellerCredit.toLocaleString()}
                %
              </Typography>
            )}
          </Box>

          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            By signing below, you agree to all terms and conditions of this
            contract. Please review the full contract document before signing.
          </Typography>

          <CommonFormControl
            label="Legal Full Name:"
            labelWidth={200}
            sx={{ mt: 2 }}
          >
            <TextField
              placeholder="Enter your legal full name"
              name="name"
              fullWidth
              value={form.name}
              onChange={handleInputChange}
              required
            />
          </CommonFormControl>

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Signature:
            </Typography>
            <UserSignatureCanvas
              sellersignature={form.signature}
              onSignatureSave={(signature) =>
                setForm((prevForm) => ({ ...prevForm, signature }))
              }
            />
          </Box>
        </CardContent>
      </Card>

      <CommonPrevNextButtons
        sx={{ mt: 3 }}
        isLoading={isSigning}
        isNextDisabled={!isValid}
        onPrev={() => navigate(-1)}
        onNext={handleSubmit}
        nextText="Sign Contract"
      />
    </Box>
  );
};
